import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Lock, Mail, ShieldCheck, Users2, CalendarCheck2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { navigateWithTransition } from "@/lib/viewTransition";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/Button";

const FEATURES = [
  { icon: CalendarCheck2, label: "Reservas y disponibilidad de pistas centralizadas" },
  { icon: Users2, label: "Admins de club y sub-usuarios con permisos a medida" },
  { icon: ShieldCheck, label: "Acceso seguro con sesiones y roles jerárquicos" },
];

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      useAuthStore.setState({ accessToken: data.accessToken });
      const { data: me } = await api.get("/auth/me");
      setSession(data.accessToken, me);
      navigate("/", { replace: true });
    } catch {
      setError("Email o contraseña incorrectos");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      headline="Gestiona tus clubes de pádel desde un único panel."
      subtext="Reservas, rankings y equipos, con roles y permisos pensados para cada club."
      features={FEATURES}
    >
      <h2 className="auth-in text-2xl font-bold text-ink-900">Bienvenido de nuevo</h2>
      <p className="auth-in mt-1 mb-8 text-sm text-ink-400" style={{ animationDelay: "60ms" }}>
        Introduce tus credenciales para entrar al panel.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="auth-in flex flex-col gap-1.5" style={{ animationDelay: "120ms" }}>
          <label htmlFor="email" className="text-sm font-medium text-ink-600">
            Email
          </label>
          <div className="group relative">
            <Mail className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-ink-200 transition-colors duration-200 group-focus-within:text-brand-500" />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="tucorreo@club.com"
              className="w-full rounded-xl border border-ink-100 bg-white py-2.5 pl-10 pr-3 text-sm shadow-sm outline-none transition-all duration-200 hover:border-ink-200 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15"
            />
          </div>
        </div>

        <div className="auth-in flex flex-col gap-1.5" style={{ animationDelay: "180ms" }}>
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="text-sm font-medium text-ink-600">
              Contraseña
            </label>
            <a
              href="/olvide-mi-contrasena"
              onClick={(e) => {
                e.preventDefault();
                navigateWithTransition(navigate, "/olvide-mi-contrasena");
              }}
              className="text-xs font-medium text-brand-600 hover:text-brand-700"
            >
              ¿Has olvidado tu contraseña?
            </a>
          </div>
          <div className="group relative">
            <Lock className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-ink-200 transition-colors duration-200 group-focus-within:text-brand-500" />
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="w-full rounded-xl border border-ink-100 bg-white py-2.5 pr-10 pl-10 text-sm shadow-sm outline-none transition-all duration-200 hover:border-ink-200 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-ink-300 transition-colors hover:text-ink-600"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

        <Button
          type="submit"
          disabled={loading}
          className="auth-in mt-2 w-full bg-gradient-to-r from-brand-500 to-brand-600 py-2.5 shadow-lg shadow-brand-600/25 transition-[transform,box-shadow,filter] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-xl hover:shadow-brand-600/30 hover:brightness-105 active:translate-y-0 active:brightness-95 disabled:hover:translate-y-0 disabled:hover:brightness-100"
          style={{ animationDelay: "240ms" }}
        >
          {loading ? "Entrando…" : "Entrar al panel"}
        </Button>
      </form>

      <div className="auth-in mt-6 flex flex-col items-center gap-2 text-sm text-ink-400" style={{ animationDelay: "300ms" }}>
        <span>¿Todavía no tienes cuenta? Solicita acceso como:</span>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1">
          {[
            { to: "/registro", label: "Proveedor (tengo un club)" },
            { to: "/registro-arbitro", label: "Árbitro" },
          ].map((opt) => (
            <a
              key={opt.to}
              href={opt.to}
              onClick={(e) => {
                e.preventDefault();
                navigateWithTransition(navigate, opt.to);
              }}
              className="group inline-flex items-center gap-0.5 font-medium text-brand-600 transition-colors hover:text-brand-700"
            >
              <span className="relative">
                {opt.label}
                <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-brand-700 transition-all duration-300 group-hover:w-full" />
              </span>
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
            </a>
          ))}
        </div>
      </div>
    </AuthLayout>
  );
}
